import { useCallback, useContext } from "react"
import { create } from "zustand"

import { DEFAULT_STOREFRONT } from "./constants"
import { useFetchConsolidatedCartProducts } from "./hooks"
import { CartContext } from "./store"
import {
  fetchCartId,
  fetchCartProducts,
  shop,
  storeCartId,
  storeCartProducts,
  storeCartPromoCode,
  StoredBundle,
  StoredItem,
  StoredItemBase,
  StoredProduct,
} from "../utils/shop"

type ReminderState = {
  pendingSignupOwnerFirstName?: string
  isReminderBannerVisible: boolean
  setPendingSignupOwnerFirstName: (pendingSignupOwnerFirstName: string) => void
  setIsReminderBannerVisible: (isReminderBannerVisible: boolean) => void
}

export const useSignupReminderStore = create<ReminderState>()((set) => ({
  pendingSignupOwnerFirstName: undefined,
  isReminderBannerVisible: false,
  setPendingSignupOwnerFirstName: (pendingSignupOwnerFirstName: string) =>
    set({ pendingSignupOwnerFirstName }),
  setIsReminderBannerVisible: (isReminderBannerVisible: boolean) =>
    set({ isReminderBannerVisible: isReminderBannerVisible }),
}))

export const useInitSignupReminder = () => {
  const {
    isReminderBannerVisible,
    setIsReminderBannerVisible,
    setPendingSignupOwnerFirstName,
  } = useSignupReminderStore()

  const urlParams = new URLSearchParams(location.search)
  const promotionParam = urlParams.get("promotion")
  const fetchProducts = useFetchConsolidatedCartProducts()
  const cartActions = useContext(CartContext)

  return useCallback(
    async ({ pendingSignupId }: { pendingSignupId: string }) => {
      if (isReminderBannerVisible || !pendingSignupId) {
        return
      }

      const signup = await shop.signups.fetchSignup(pendingSignupId)

      if (!signup) {
        return
      }

      setPendingSignupOwnerFirstName(signup.owner_first_name)
      setIsReminderBannerVisible(true)

      const validExistingCart = !!fetchCartId() && !!fetchCartProducts()?.length

      if (validExistingCart) {
        return
      }

      // We pull in the existing cart from a hello signup if
      // there isnt currently a valid cart on yoco-com

      const signupCart = await shop.carts.get(signup.cart_uuid)

      // These items still need storyblok uuuid's
      const newStoredItems = signupCart.items.reduce(
        (acc: StoredItem[], item) => {
          if (item.product) {
            return [
              ...acc,
              {
                sku: item.product.sku,
                quantity: item.quantity,
                type: "product",
                storefront: DEFAULT_STOREFRONT,
              } as StoredProduct,
            ]
          } else if (item.bundle) {
            return [
              ...acc,
              {
                bundleCode: item.bundle.bundle_code,
                quantity: item.quantity,
                type: "bundle",
                storefront: DEFAULT_STOREFRONT,
              } as StoredBundle,
            ]
          }
          return acc
        },
        []
      )

      const consolidatedProducts = fetchProducts(newStoredItems, {
        findBySkuOrCode: true,
      })

      // Add storyblok uuid's so that we can store in localstorage
      const storedCartProducts = newStoredItems.reduce(
        (acc: StoredItemBase[], newStoredItem) => {
          const consolidatedProduct = consolidatedProducts.find(
            (consolidatedProduct) =>
              consolidatedProduct.type === "product"
                ? consolidatedProduct.sku
                : consolidatedProduct.bundleCode
          )

          if (consolidatedProduct) {
            return [
              ...acc,
              {
                ...newStoredItem,
                id: consolidatedProduct.id,
              },
            ]
          }

          return acc
        },
        []
      )

      cartActions.setCartId(signup.cart_uuid)
      cartActions.setProducts(consolidatedProducts)

      storeCartId(signup.cart_uuid)
      storeCartProducts(storedCartProducts)

      if (promotionParam && typeof promotionParam === "string") {
        storeCartPromoCode(promotionParam)
      }
    },
    [
      cartActions,
      fetchProducts,
      isReminderBannerVisible,
      promotionParam,
      setIsReminderBannerVisible,
      setPendingSignupOwnerFirstName,
    ]
  )
}
